"use client";

/**
 * Provides a single `openPaywall()` entry point for anywhere in the app that
 * needs to trigger the subscribe modal (gated feature tap, locked card,
 * Manage Billing row without a Stripe customer, etc.).
 *
 * Pre-paywall coverage gate (Decision #2 in
 * [[shared-subscription-any-household-adult-can-pay-co]]): when every
 * household the caller is in already has an active subscription, openPaywall
 * shows the AlreadyCoveredModal instead. This prevents accidental redundant
 * subscriptions.
 *
 * Mounted near the top of `(app)/layout.tsx` so one modal instance serves the
 * whole authenticated app.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { PaywallModal } from "@/components/features/PaywallModal";
import { AlreadyCoveredModal } from "@/components/features/AlreadyCoveredModal";
import { useCallerCoverage } from "@/hooks/useCallerCoverage";
import type { PricingTier } from "@/lib/pricingTiers";

interface PaywallOptions {
  focusTier?: PricingTier["id"];
  title?: string;
  subtitle?: string;
}

interface PaywallContextValue {
  openPaywall: (opts?: PaywallOptions) => void;
  closePaywall: () => void;
  isOpen: boolean;
}

const PaywallContext = createContext<PaywallContextValue | null>(null);

export function PaywallProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coveredOpen, setCoveredOpen] = useState(false);
  const [opts, setOpts] = useState<PaywallOptions>({});
  const coverage = useCallerCoverage();
  const [searchParams, setSearchParams] = useSearchParams();

  const openPaywall = useCallback(
    (next?: PaywallOptions) => {
      setOpts(next ?? {});
      // Pre-paywall coverage gate. If every household the caller is in is
      // already covered, route to the AlreadyCoveredModal instead. Skip the
      // gate while coverage is still loading — paywall opens normally and
      // the server-side gate in createCheckoutSession will catch a redundant
      // attempt as a fail-safe.
      if (!coverage.loading && coverage.allHouseholdsCovered) {
        setCoveredOpen(true);
        return;
      }
      setIsOpen(true);
    },
    [coverage.loading, coverage.allHouseholdsCovered],
  );

  // URL trigger: `?paywall=1` opens the generic paywall on mount;
  // `?paywall=zoey_plus` (or any tier id) opens focused on that tier.
  // Used by the Android billing handoff (Chrome Custom Tab → web app)
  // to land users with no Stripe customer record directly on the
  // subscribe flow rather than on a static Manage Billing row.
  // Strips the param after firing so a refresh doesn't re-open.
  useEffect(() => {
    const param = searchParams.get("paywall");
    if (!param) return;
    if (coverage.loading) return; // wait until we know if all-covered
    openPaywall(
      param !== "1"
        ? { focusTier: param as PricingTier["id"] }
        : undefined,
    );
    const next = new URLSearchParams(searchParams);
    next.delete("paywall");
    setSearchParams(next, { replace: true });
  }, [searchParams, coverage.loading, openPaywall, setSearchParams]);

  const closePaywall = useCallback(() => {
    setIsOpen(false);
  }, []);

  const closeCovered = useCallback(() => {
    setCoveredOpen(false);
  }, []);

  return (
    <PaywallContext.Provider value={{ openPaywall, closePaywall, isOpen }}>
      {children}
      <PaywallModal
        isOpen={isOpen}
        onClose={closePaywall}
        focusTier={opts.focusTier}
        title={opts.title}
        subtitle={opts.subtitle}
      />
      <AlreadyCoveredModal
        isOpen={coveredOpen}
        onClose={closeCovered}
        entries={coverage.entries}
      />
    </PaywallContext.Provider>
  );
}

export function usePaywall(): PaywallContextValue {
  const ctx = useContext(PaywallContext);
  if (!ctx)
    throw new Error("usePaywall must be used within a PaywallProvider");
  return ctx;
}
